import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CheckCircle, Close, Error } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, CircularProgress, IconButton, Paper, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton, NavigationButton, Progress } from "ui";

import { apiSlice, useDeleteLayerMutation } from "fond/api";
import { selectLayerByVersionAndLayerKey, useLazyGetFeaturesQuery } from "fond/api/layersSlice";
import { useFeatureFlag } from "fond/featureFlags";
import { getAllAcceptedExtensions, validateFilePackage } from "fond/fileValidation";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { refreshTileSource } from "fond/map/redux";
import BomModal from "fond/project/bom/BomModal";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ImportStatus, MuPowerOutputLayerIds, MuPowerSolverLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { getFilesFromEvent } from "fond/utils/files";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, LinearProgressWithLabel, LogViewerModal, SvgIcon } from "fond/widgets";

import UploadModal from "./InputPanel/UploadModal";

const stageConfig: Record<string, { label: string }> = {
  power: { label: "Generating power design" },
  bom: { label: "Generating bill of materials" },
};

const solverInputLayers = [
  { key: MuPowerSolverLayerIds.sourceHubs, title: "Source Hubs", icon: "points" as const, required: true },
  { key: MuPowerSolverLayerIds.primaryFootprint, title: "Primary Footprint", icon: "line" as const, required: false },
  { key: MuPowerSolverLayerIds.primaryBlockers, title: "Primary Blockers", icon: "line" as const, required: false },
  { key: MuPowerSolverLayerIds.secondaryFootprint, title: "Secondary Footprint", icon: "line" as const, required: false },
  { key: MuPowerSolverLayerIds.secondaryBlockers, title: "Secondary Blockers", icon: "line" as const, required: false },
];

interface SolverLayerButtonProps {
  layer: (typeof solverInputLayers)[number];
  layerStatus: LayerStatus | undefined;
  disabled: boolean;
  isReadOnly?: boolean;
  isInProgress: boolean;
  onUpload: (layerKey: string, files: File[]) => void;
  onClear: (layerKey: string) => void;
  onClick: () => void;
}

const SolverLayerButton: React.FC<SolverLayerButtonProps> = ({
  layer,
  layerStatus,
  disabled,
  isReadOnly,
  isInProgress,
  onUpload,
  onClear,
  onClick,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: getAllAcceptedExtensions(),
    getFilesFromEvent,
    disabled,
    onDropAccepted: (files) => {
      const { isPackageValid, error } = validateFilePackage(files);
      if (isPackageValid) {
        onUpload(layer.key, files);
      } else {
        enqueueSnackbar(error?.message || "Invalid file package", { variant: "error" });
      }
    },
  });

  const hasLayer = !!layerStatus?.layer;
  const isError = layerStatus?.status === ImportStatus.ERROR;
  const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

  return (
    <Box
      {...getRootProps()}
      sx={{
        borderRadius: 1,
        transition: "outline 0.15s",
        outline: isDragActive ? "2px dashed" : "2px dashed transparent",
        outlineColor: isDragActive ? "primary.main" : "transparent",
      }}
    >
      <NavigationButton
        data-testid={`mu-power-${layer.key.split("/").pop()}-input`}
        icon={<SvgIcon icon={layer.icon} />}
        title={layer.title}
        onClick={onClick}
        sx={{ m: 0, mt: 1 }}
        disabled={disabled}
        checked={isComplete}
        rightElement={
          isError ? (
            <Tooltip title="Import failed">
              <Error color="error" sx={{ fontSize: 18 }} />
            </Tooltip>
          ) : isComplete ? (
            <Box display="flex" alignItems="center">
              <CheckCircle color="success" sx={{ fontSize: 18 }} />
              {layer.required ? (
                <IconButton size="small" sx={{ ml: 0.5, p: 0.25, visibility: "hidden" }}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              ) : (
                <Tooltip title="Remove layer">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear(layer.key);
                    }}
                    disabled={isReadOnly || isInProgress}
                    data-testid={`mu-power-${layer.key.split("/").pop()}-clear`}
                    sx={{ ml: 0.5, p: 0.25 }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : layer.required ? (
            <Tooltip title="Required">
              <Error sx={{ color: "warning.main", fontSize: 18 }} />
            </Tooltip>
          ) : null
        }
      />
    </Box>
  );
};

interface PowerSolverPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
  solverLayers: Map<string, LayerStatus>;
  project: Project;
}

const PowerSolverPanel: React.FC<PowerSolverPanelProps> = ({ isReadOnly, canRun, solverLayers, project }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [uploadingLayer, setUploadingLayer] = useState<string | null>(null);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);
  const [deleteLayer] = useDeleteLayerMutation();
  const [fetchFeatures] = useLazyGetFeaturesQuery();

  const primaryWiresLayer = useAppSelector((state) =>
    selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerOutputLayerIds.primaryWires })
  );
  const secondaryWiresLayer = useAppSelector((state) =>
    selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerOutputLayerIds.secondaryWires })
  );

  const { workflowState, stages, runWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER,
  });
  const { value: isCustomWorkflowErrorLogsEnabled } = useFeatureFlag("custom_workflow_error_logs");

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const isAnyUploading = Array.from(solverLayers.values()).some(
    (layer) => layer.status && ["CONVERTING", "IMPORTING", "BUILDING_TILES"].includes(layer.status)
  );

  const allRequiredUploaded = solverInputLayers.filter((l) => l.required).every((l) => !!solverLayers.get(l.key)?.layer);

  useEffect(() => {
    if (isAnyUploading) {
      if (isLocking) setIsLocking(false);
      if (isUploading) setIsUploading(false);
    }
  }, [isLocking, isUploading, isAnyUploading]);

  const handleUpload = (layerKey: string, files: File[]) => {
    const baseName = files[0]?.name.split(".")[0].toLowerCase() || layerKey.split("/").pop() || "Layer";
    setIsUploading(true);
    dispatch(
      addImport({
        versionId,
        layerKey,
        displayName: baseName,
        files,
      })
    );
    setUploadingLayer(null);
  };

  const handleClear = async (layerKey: string) => {
    const layerId = solverLayers.get(layerKey)?.layer?.ID;
    if (!layerId) return;
    await deleteLayer(layerId);
    dispatch(
      apiSlice.util.invalidateTags([
        { type: "Layers", id: versionId },
        { type: "FeatureTotals", id: versionId },
      ])
    );
    dispatch(refreshTileSource("project-layers"));
  };

  const performLock = async () => {
    setShowLockConfirm(false);
    setIsLocking(true);

    const layers = [
      { outputLayer: primaryWiresLayer, targetKey: MuPowerSolverLayerIds.primaryFootprint },
      { outputLayer: secondaryWiresLayer, targetKey: MuPowerSolverLayerIds.secondaryFootprint },
    ];

    await Promise.all(
      layers.map(async ({ outputLayer, targetKey }) => {
        if (!outputLayer?.ID) return;
        const { data: features } = await fetchFeatures({ layerId: outputLayer.ID });
        if (!features?.length) return;

        const file = new File([JSON.stringify(featureCollection(features))], `${targetKey.split("/").pop()}.geojson`, {
          type: "application/geo+json",
        });

        await dispatch(
          addImport({
            versionId,
            layerKey: targetKey,
            displayName: targetKey.split("/").pop() || targetKey,
            files: [file],
          })
        );
      })
    );
  };

  const handleLockFootprint = () => {
    const hasPrimaryFootprint = !!solverLayers.get(MuPowerSolverLayerIds.primaryFootprint)?.layer;
    const hasSecondaryFootprint = !!solverLayers.get(MuPowerSolverLayerIds.secondaryFootprint)?.layer;

    if (hasPrimaryFootprint || hasSecondaryFootprint) {
      setShowLockConfirm(true);
    } else {
      performLock();
    }
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation required</AlertTitle>
          Please complete data preparation before running the power solver.
        </Alert>
      )}

      {solverInputLayers.map((layer) => (
        <SolverLayerButton
          key={layer.key}
          layer={layer}
          layerStatus={solverLayers.get(layer.key)}
          disabled={isReadOnly || isAnyUploading || isUploading || isInProgress}
          isReadOnly={isReadOnly || isAnyUploading || isUploading}
          isInProgress={isInProgress}
          onUpload={handleUpload}
          onClear={handleClear}
          onClick={() => setUploadingLayer(layer.key)}
        />
      ))}

      {(isAnyUploading || isUploading) && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mt: 2 }}>
          <AlertTitle>Power solver failed</AlertTitle>
          There was an error running the power solver. Please try again.
          {isCustomWorkflowErrorLogsEnabled && (
            <Box mt={1}>
              {logs?.type === "waiting" && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={12} color="inherit" />
                  Attempting to process logs
                </Box>
              )}
              {logs?.type === "ready" && logs.logs.length === 0 && "Error logs are not available."}
              {logs?.type === "ready" && logs.logs.length > 0 && (
                <Button size="small" variant="outlined" color="error" onClick={() => setIsLogsModalOpen(true)}>
                  View Logs
                </Button>
              )}
            </Box>
          )}
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mt: 2 }}>
          Power design complete.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <LoadingButton
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          loading={isLocking}
          disabled={isReadOnly || isAnyUploading || isInProgress}
          onClick={handleLockFootprint}
        >
          Lock footprints
        </LoadingButton>
      )}

      {workflowState === "COMPLETE" && (
        <Button fullWidth variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setIsBomModalOpen(true)}>
          View bill of materials
        </Button>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      <Button
        fullWidth
        variant="contained"
        size="small"
        sx={{ mt: 2 }}
        disabled={!runWorkflow || !canRun || !allRequiredUploaded || isInProgress || isAnyUploading || isUploading || isReadOnly}
        onClick={() => runWorkflow?.()}
      >
        {workflowState === "COMPLETE" ? "Rerun" : "Start"}
      </Button>

      {uploadingLayer && (
        <UploadModal
          layerTitle={solverInputLayers.find((l) => l.key === uploadingLayer)?.title || "Layer"}
          onClose={() => setUploadingLayer(null)}
          onSubmit={(files) => handleUpload(uploadingLayer, files)}
        />
      )}

      <ConfirmModal
        open={showLockConfirm}
        content="The Primary Footprint and/or Secondary Footprint layers already have data. Importing will replace the existing data. Continue?"
        confirmText="Continue"
        onConfirm={performLock}
        onCancel={() => setShowLockConfirm(false)}
      />

      {isCustomWorkflowErrorLogsEnabled && logs?.type === "ready" && (
        <LogViewerModal open={isLogsModalOpen} onClose={() => setIsLogsModalOpen(false)} logs={logs.logs} title="Logs" />
      )}

      {isBomModalOpen && <BomModal project={project} architecture={null} onClose={() => setIsBomModalOpen(false)} />}
    </>
  );
};

export default PowerSolverPanel;
